import { Coupon } from '@app/components/icons/coupon';
import { Play } from '@app/components/icons/play';
import { useFlags } from '@app/hooks/use-flags';
import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useState } from 'react';
import { Arrow, useLayer } from 'react-laag';

import { FLAG_ENABLE_CH_DASHBOARD_ANALYTICS } from '../../constants';
import { useAnalytics } from '../../hooks/use-analytics';
import { EventData, useEventsAttended } from '../../hooks/use-events-attended';
import { BranchIcon } from '../../icons/branch-icon';
import { BranchStartIcon } from '../../icons/branch-start-icon';
import { CollapsedSmallIcon } from '../../icons/collapsed-small';
import { ExpandedSmallIcon } from '../../icons/expanded-small';
import { useAnalyticsContext } from '../../providers/analytics-provider';
import { InterContentStyles, InterHeaderStyles } from '../../styles';
import { formatDate, navigateToEvent } from '../../utils';

const LoaderSkeleton = ({ count = 3 }) => {
  return (
    <>
      {new Array(count).fill(0).map((_, id) => (
        <div
          key={id}
          className="px-3 py-[7px] flex flex-col w-full"
          data-testid="skeleton-item"
        >
          <div className="w-full">
            <div className="h-5 animate-pulse bg-gray-200 rounded-md">
              &nbsp;
            </div>
            <div className="h-4 mt-1 animate-pulse bg-gray-200 rounded-md">
              &nbsp;
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export const PopoverMenu = ({
  count,
  email,
  slug,
  icon,
}: {
  count: number;
  email: string;
  slug: string;
  icon?: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSeries, setExpandedSeries] = useState<Set<string>>(new Set());
  const { state } = useAnalyticsContext();
  const flags = useFlags();

  const enableCHDashboardAnalytics =
    flags?.[FLAG_ENABLE_CH_DASHBOARD_ANALYTICS];

  const config = {
    dateRange: state.dateRange,
    startDate: state.startDate,
    endDate: state.endDate,
    eventTypes: state.eventTypes,
    ...(!enableCHDashboardAnalytics && {
      searchText: state.searchText,
    }),
    emailId: email,
    ...(enableCHDashboardAnalytics && {
      includeContacts: true,
      includeUpcoming: true,
    }),
  };
  const orgId: string = useReadDashboardSharedState('org_id') || '';
  const { data, isLoading, error } = useEventsAttended(
    { ...config },
    `eventData-${orgId}`,
    slug,
    isOpen,
  );

  const { renderLayer, triggerProps, layerProps, arrowProps } = useLayer({
    isOpen,
    onOutsideClick: () => setIsOpen(false),
    placement: 'right-center',
    triggerOffset: 8,
    auto: true,
  });

  const { track } = useAnalytics();

  const beforeMenuOpen = () => {
    setIsOpen(!isOpen);
    track('ola_registrants_tab_events_registered_plus_click', {});
  };

  const getActivityTypeIcon = (activityType: string | undefined) => {
    if (!enableCHDashboardAnalytics) return null;
    switch (activityType) {
      case 'event':
        return <Coupon className="size-4 flex-shrink-0" />;
      case 'video':
        return <Play className="size-4 flex-shrink-0" />;
      default:
        return null;
    }
  };

  const getActivityTimeText = (item: EventData) => {
    if (item.activityType === 'video') return formatDate(item.startTime);
    return `${formatDate(item.startTime)} -> ${formatDate(item.endTime)}`;
  };

  const toggleSeriesExpansion = (seriesId: string) => {
    const newExpanded = new Set(expandedSeries);
    if (newExpanded.has(seriesId)) {
      newExpanded.delete(seriesId);
    } else {
      newExpanded.add(seriesId);
    }
    setExpandedSeries(newExpanded);
  };

  const groupEventsBySeries = (events: EventData[]) => {
    const seriesGroups: {
      [key: string]: {
        series: { externalId: string; name: string };
        events: (EventData & { count: number })[];
      };
    } = {};
    const standaloneEvents: (EventData & { count: number })[] = [];

    const eventCounts = events.reduce(
      (acc: { [key: string]: EventData & { count: number } }, curr) => {
        if (acc[curr.slug]) {
          acc[curr.slug].count++;
        } else {
          acc[curr.slug] = { ...curr, count: 1 };
        }
        return acc;
      },
      {},
    );

    Object.values(eventCounts).forEach(event => {
      if (event.eventSeries && event.eventSeries.length > 0) {
        // Add event to each series it belongs to
        event.eventSeries.forEach(series => {
          if (!seriesGroups[series.externalId]) {
            seriesGroups[series.externalId] = {
              series,
              events: [],
            };
          }
          // Only add if not already added to this series
          const existsInSeries = seriesGroups[series.externalId].events.some(
            e => e.slug === event.slug,
          );
          if (!existsInSeries) {
            seriesGroups[series.externalId].events.push(event);
          }
        });
      } else {
        standaloneEvents.push(event);
      }
    });

    return { seriesGroups, standaloneEvents };
  };

  const renderEventItem = (
    item: EventData & { count: number },
    isSubItem = false,
    index?: number,
  ) => (
    <button
      className={cx('flex flex-col hover:bg-blue-100 w-full relative', {
        'cursor-default': item.activityType !== 'event',
        'px-3 py-2': !isSubItem,
        'py-0': isSubItem,
        'px-0': isSubItem,
      })}
      key={item.slug}
      onClick={() => {
        if (item.activityType === 'event') {
          navigateToEvent(item.slug);
        }
      }}
    >
      <div
        className={cx(
          `${InterHeaderStyles} text-left flex flex-row items-start`,
          {
            'pl-3': isSubItem,
          },
        )}
      >
        {isSubItem && (
          <div
            className={`absolute left-5 ${
              index === 0 ? 'top-0' : 'top-[-35px]'
            } z-10 flex-shrink-0`}
          >
            {index === 0 ? (
              <BranchStartIcon className="w-3.5 h-8" />
            ) : (
              <BranchIcon className="w-3.5 h-full min-h-[2rem]" />
            )}
          </div>
        )}
        <div
          className={cx('flex flex-col gap-1 flex-1 min-w-0', {
            'ml-7': isSubItem,
          })}
        >
          <div className="flex flex-row gap-1 items-center py-1">
            {getActivityTypeIcon(item.activityType)}
            <span className="truncate">{item.name}</span>
            {item.count > 1 && (
              <span className="text-xs flex items-center justify-center text-current bg-blue-500 text-white rounded-full px-2 h-4 w-auto ml-1">
                {item.count}
              </span>
            )}
          </div>
          <div className={`${InterContentStyles} text-left pb-1`}>
            {getActivityTimeText(item)}
          </div>
        </div>
      </div>
    </button>
  );

  const renderSeriesItem = (
    seriesId: string,
    seriesData: {
      series: { externalId: string; name: string };
      events: (EventData & { count: number })[];
    },
  ) => {
    const isExpanded = expandedSeries.has(seriesId);

    return (
      <div key={seriesId}>
        <button
          className="px-3 py-1.5 flex flex-col hover:bg-blue-100 w-full"
          onClick={() => toggleSeriesExpansion(seriesId)}
        >
          <div
            className={`${InterHeaderStyles} text-left flex flex-row gap-1 items-center justify-between w-full`}
          >
            <div className="flex flex-row gap-1 items-center flex-1 min-w-0">
              <span className="truncate font-medium">
                {seriesData.series.name}
              </span>
            </div>
            <div className="flex-shrink-0 ml-2">
              {isExpanded ? <ExpandedSmallIcon /> : <CollapsedSmallIcon />}
            </div>
          </div>
        </button>
        {isExpanded &&
          seriesData.events.map((event, index) =>
            renderEventItem(event, true, index),
          )}
      </div>
    );
  };

  return (
    <>
      <button
        {...triggerProps}
        onClick={beforeMenuOpen}
        className={`relative px-2 py-1 rounded-2xl bg-active font-inter font-medium text-sm whitespace-nowrap overflow-visible flex items-center gap-1`}
      >
        {icon ? icon : null}
        <span>{`+${count}`}</span>
      </button>

      {isOpen &&
        renderLayer(
          <div
            {...layerProps}
            className="p-2 z-50 bg-white border rounded-[10px] shadow-md max-h-72 w-72 overflow-y-auto overflow-x-hidden scrollbar-hide"
            data-testid="dialog"
          >
            {isLoading && <LoaderSkeleton count={count} />}
            {error && (
              <div className=" m-2 font-inter font-medium break-words text-sm">
                {t('analytics.error-message')}
              </div>
            )}
            {!!data &&
              (() => {
                const filteredData = data.filter(
                  item =>
                    enableCHDashboardAnalytics || item.activityType === 'event',
                );

                const { seriesGroups, standaloneEvents } =
                  groupEventsBySeries(filteredData);

                return (
                  <>
                    {standaloneEvents.map(event => renderEventItem(event))}
                    {Object.entries(seriesGroups).map(
                      ([seriesId, seriesData]) =>
                        renderSeriesItem(seriesId, seriesData),
                    )}
                  </>
                );
              })()}
            <Arrow {...arrowProps} size={6} roundness={0} />
          </div>,
        )}
    </>
  );
};
